import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { map, mergeMap, switchMap, publishReplay, refCount } from 'rxjs/operators';
import { dataMember, typedArray } from 'santee-dcts';
import { Settings } from './../../../core';
import { ReportData, GridFilter, ReportRequestInfo } from '../interfaces/report-data';
import { Reports, ScheduleReport } from '../interfaces/reports';
import { ReportUpdateRequest } from '../interfaces/report-update-request';

export class ReportListModel {
    @dataMember()
    @typedArray(ReportData)
    public reportList: ReportData[];
}

export class ReportCountModel {
    @dataMember()
    public count: number[];
}

@Injectable()
export class ReportsStorageService {

    public gridFilter: Observable<GridFilter>;
    public reportUrl = `${this.settings.apiUrl}/sponsor/sampletracker/reports`;
    private readonly newReportsCount: Observable<number> = of(0);
    private loadNewReportCount$ = new BehaviorSubject<boolean>(true);
    constructor(
        private settings: Settings,
        private http: HttpClient) {

        this.newReportsCount = this.loadNewReportCount$
            .pipe(
                mergeMap(() => of(this.getNewReportCount())),
                switchMap(x => x),
                publishReplay(1),
                refCount()
            );
    }

    public get reportCount() {
        return this.newReportsCount;
    }

    public forceLoadReportCount() {
        this.loadNewReportCount$.next(true);
    }

    public getGeneratedReports(requestInfo: ReportRequestInfo): Observable<ReportData[]> {
        return this.http.post(`${this.reportUrl}/list`, requestInfo).pipe(
            map((rawData: ReportListModel) => {
                return rawData.reportList;
            })
        );
    }

    public getScheduledReports(requestInfo: ReportRequestInfo): Observable<ReportData[]> {
        return this.http.post(`${this.reportUrl}/scheduledlist`, requestInfo).pipe(
            map((rawData: ReportListModel) => {
                return rawData.reportList;
            })
        );
    }

    public getReports(): Observable<any> {
        return this.http.get(`${this.reportUrl}`);
    }

    public getReportById(reportId: number): Observable<any> {
        return this.http.get(`${this.reportUrl}/${reportId}`);
    }

    public isGenerateReportValid(reportData: Reports): Observable<any> {
        return this.http.post(`${this.reportUrl}/users-filters-count`, reportData);
    }

    public generateReports(reportData: Reports): Observable<any> {
        return this.http.post(`${this.reportUrl}/generate`, reportData);
    }

    public scheduleReports(schedule: ScheduleReport): Observable<any> {
        return this.http.post(`${this.reportUrl}/schedule`, schedule);
    }

    public deleteReport(reportId: string): Observable<any> {
        return this.http.delete(`${this.reportUrl}/delete/${reportId}`);
    }

    public getNewReportCount(): Observable<number> {
        return this.http.get(`${this.reportUrl}/new/count`).pipe(
            map((rawData: ReportCountModel) => {
                if (!rawData.count || rawData.count.length === 0) {
                    return 0;
                }
                return rawData.count[0];
            })
        );
    }

    public async updateReportActivity(reportUpdateRequest: ReportUpdateRequest) {
        return this.http.post(`${this.reportUrl}/update`, reportUpdateRequest);
    }

    public async runReport(reportId: number, reportRunTime: string, reportContent: string, filterContent: string) {
        return this.http.post(`${this.reportUrl}/run`, {
            reportId: reportId,
            reportContent: reportContent,
            filterContent: filterContent,
            reportRunTime: reportRunTime
        });
    }

    public async deleteExistingReport(reportId: number) {
        return this.http.delete(`${this.reportUrl}/scheduledReport/delete/${reportId}`);
    }

    public async updateReportReadStatus(reportId: number) {
        return this.http.get(`${this.reportUrl}/read/${reportId}`);
    }

    public getDynamicListViewColumnInfo(listViewName: string): Observable<any> {
        return this.http.get(`${this.settings.apiUrl}/sponsor/sampletracker/grouptracking/columnpickerdata/${listViewName}`).pipe(
            map((result: any) => {
                return result.gridDataColumnPickerData;
            }
        ));
    }

    public async resetColumnConfig(reportId: any): Promise<void> {
        await this.http.delete(`${this.settings.apiUrl}/sponsor/sampletracker/reports/reset/${reportId}`).toPromise();
    }

    public deleteGeneratedReports(reports: ReportData[]) {
        let requestData: any[] = [];
        reports.forEach(t => {
            requestData.push({ reportId: t.id, reportGuid: t.reportID.trim() });
        });
        return this.http.post(`${this.reportUrl}/generatedReport/delete`, requestData);
    }
}